import { getCurrentUserToken } from "@/lib/auth";
import { estimatePlanIntervalChange } from "@/lib/pricing/estimatePlanIntervalChange";
import { TBillingInterval } from "@/types/app";
import { toMinorUnits } from "@/utils/toMinorUnits";
import { useQuery } from "@tanstack/react-query";
import {
  EstimateIntervalData,
  estimateIntervalChange,
} from "./estimate-interval-change";

interface UseEstimateIntervalChangeQueryProps {
  targetInterval: TBillingInterval;
  promoCode?: string;
  enabled?: boolean;
  isV1?: boolean;
  isLocalisedPricing?: boolean;
  /** The currency the V1 estimate answers in — unused on the V2 path. */
  currency?: string;
  /** Keeps the last estimate while a new promo code's estimate loads */
  keepPreviousData?: boolean;
}

export const useEstimateIntervalChangeQuery = ({
  targetInterval,
  promoCode = "",
  enabled = false,
  isV1 = false,
  isLocalisedPricing = false,
  currency,
  keepPreviousData = false,
}: UseEstimateIntervalChangeQueryProps) => {
  return useQuery<EstimateIntervalData>({
    queryKey: [
      "estimate-interval-change",
      targetInterval,
      promoCode,
      currency,
      // Keyed so a USD estimate cached before the region resolved isn't reused.
      isLocalisedPricing,
    ],
    queryFn: async () => {
      const token = await getCurrentUserToken();
      if (!token) throw new Error("No auth token");

      if (isV1) {
        const v1Interval = targetInterval === "yearly" ? "year" : "month";
        const res = await estimatePlanIntervalChange({
          token,
          interval: v1Interval,
          ...(promoCode.trim() ? { couponValue: promoCode.trim() } : {}),
        });

        return {
          // V1 answers in major units; the rest of the flow expects minor.
          currency,
          amount_due: toMinorUnits(res.amount ?? 0, currency),
          message: res.message ?? "",
          discounts: res.coupon_code
            ? [
                {
                  coupon_code: res.coupon_code,
                  percent_off: null,
                  amount_off: null,
                  invalid: false,
                  invalid_reason: "",
                },
              ]
            : [],
        };
      }

      const res = await estimateIntervalChange({
        token,
        payload: {
          change_to: targetInterval,
          estimate: true,
          ...(promoCode.trim() ? { discounts: [promoCode.trim()] } : {}),
        },
        isLocalisedPricing,
      });

      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled,
    keepPreviousData,
    retry: 1,
    staleTime: 0,
  });
};
